import React, { ChangeEvent, useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Checkbox, IconButton, ListItem, Stack, TextField, Tooltip } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';

import { InclusionForm } from './DealCrawlerInclusionList';

type Props = {
  inclusion: InclusionForm;
  onEditInclusion: (oldName: string, newInclusion: InclusionForm) => void;
  isEditEnabled?: boolean;
};

function DealCrawlerInclusionListItem({ inclusion, onEditInclusion, isEditEnabled }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(inclusion.description);

  const isDisabled = !inclusion.isActive;

  const handleCancelEdit = useCallback(() => {
    setEditedValue(inclusion.description);
    setIsEditing(false);
  }, [inclusion.description]);

  const handleSaveEdit = useCallback(() => {
    onEditInclusion(inclusion.description, { ...inclusion, description: editedValue });
    setIsEditing(false);
  }, [inclusion, editedValue, onEditInclusion, setIsEditing]);

  const handleToggleInclusion = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onEditInclusion(inclusion.description, { ...inclusion, isActive: e.target.checked });
    },
    [inclusion, onEditInclusion],
  );

  return (
    <ListItem
      key={inclusion.description}
      secondaryAction={
        <Stack direction="row" gap={1} alignItems="center">
          {!isEditing && isEditEnabled && (
            <>
              <Tooltip title="Edit Inclusion">
                <IconButton onClick={() => setIsEditing(true)} color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={isDisabled ? 'Add item' : 'Remove item'}>
                <Checkbox
                  checked={inclusion.isActive}
                  onChange={handleToggleInclusion}
                  sx={{
                    color: 'primary.main',
                  }}
                />
              </Tooltip>
            </>
          )}

          {isEditing && (
            <>
              <Tooltip title="Save Inclusion">
                <IconButton onClick={handleSaveEdit} color="primary">
                  <SaveIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancel Edit">
                <IconButton onClick={handleCancelEdit} color="error">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      }
    >
      {isEditing && (
        <TextField
          defaultValue={inclusion.description}
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
          sx={{ width: '50%' }}
        />
      )}

      {!isEditing && (
        <ListItemText primary={inclusion.description} sx={{ textDecoration: isDisabled ? 'line-through' : 'none' }} />
      )}
    </ListItem>
  );
}

export default DealCrawlerInclusionListItem;
